import type React from 'react';
import {
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';

import Button from '../button/button';
import type { ButtonVariant } from '../button/types';
import Form from '../forms/form';
import type { ModelType } from '../forms/form_context/form_context';
import type { FormErrors } from '../forms/types';
import Modal, { type ModalSizes, type ModalVariant } from '../modal/modal';
import SendHint from '../send_hint/send_hint';

import styles from './controls_modal.module.css';

type ButtonVariants = {
  [variant in ModalVariant]?: ButtonVariant;
};

const cancelButtonVariants: ButtonVariants = {
  danger: 'default',
  warning: 'warning',
};

const confirmButtonVariants: ButtonVariants = {
  danger: 'danger',
  warning: 'danger',
};

type CommonProps = {
  isOpen: boolean;
  isDisabled?: boolean;
  isConfirmDisabled?: boolean;
  headerText?: string | ReactElement;
  size?: ModalSizes;
  cancelButtonText?: string;
  cancelButtonVariant?: ButtonVariant;
  confirmButtonText?: string;
  confirmButtonVariant?: ButtonVariant;
  backButtonText?: string;
  onOpen?: () => void;
  // Called after the modal is closed and animation has ended,
  // good place to clean up the modal state
  onAfterClose?: () => void;
  onCancel: (
    event?: React.MouseEvent | React.KeyboardEvent,
    options?: { warn: boolean },
  ) => void;
  onCancelButtonClick?: () => void;
  onBack?: () => void;
  variant?: ModalVariant;
  hideSendHint?: boolean;
  className?: string;
  submitAndClearButtonText?: string;
  onSubmitAndClear?: () => void;
  confirmAndClearButtonVariant?: ButtonVariant;
  beforeConfirmElement?: ReactElement;
};

export type NotFormProps = CommonProps & {
  isForm?: false;
  onConfirm?: () => Promise<unknown>;
};

type FormProps<FormData extends object> = CommonProps & {
  isForm: true;
  warnIfFormChanges?: boolean;
  isFormVertical?: boolean;
  onConfirm?: (
    data: FormData,
    reset?: (model?: FormData) => void,
  ) => Promise<unknown>;
  validationErrors?: FormErrors;
  // `name` is the field's dotted path into the model, not a key of it.
  onChange?: <V>(data: FormData, name: string | undefined, value: V) => void;
};

export type ControlsModalProps<FormData extends object = ModelType> =
  | PropsWithChildren<NotFormProps>
  | PropsWithChildren<FormProps<FormData>>;

const ControlsModal = <FormData extends object = ModelType>(
  props: ControlsModalProps<FormData>,
): ReactElement => {
  const {
    cancelButtonText = 'Cancel',
    confirmButtonText = 'OK',
    backButtonText = 'Back',
    onOpen,
    onAfterClose,
    isForm,
    variant = 'success',
    headerText,
    size,
    isOpen,
    children,
    isConfirmDisabled = false,
    onCancel,
    onConfirm,
    onBack,
    className,
    hideSendHint,
    submitAndClearButtonText = 'Submit And clear',
    onSubmitAndClear,
    beforeConfirmElement,
  } = props;

  const cancelButtonVariant =
    props.cancelButtonVariant ?? cancelButtonVariants[variant] ?? 'warning';
  const confirmButtonVariant =
    props.confirmButtonVariant ?? confirmButtonVariants[variant] ?? 'success';

  const confirmAndClearButtonVariant =
    props.confirmAndClearButtonVariant ?? 'info';

  const [isHandlingConfirm, setIsHandlingConfirm] = useState(false);
  const [updated, setUpdated] = useState(false);

  const handleConfirm = useCallback(
    (
      formData?: FormData,
      reset?: (model?: FormData | undefined) => void,
    ): Promise<unknown> | undefined => {
      if (!onConfirm) {
        return undefined;
      }
      let confirmPromise: Promise<unknown> | undefined;
      if (isForm) {
        if (formData) {
          confirmPromise = onConfirm(formData, reset);
        }
      } else {
        confirmPromise = onConfirm();
      }
      if (confirmPromise instanceof Promise) {
        setIsHandlingConfirm(true);
        confirmPromise.finally(() => setIsHandlingConfirm(false));
      }
      return confirmPromise;
    },
    [isForm, onConfirm],
  );

  const handleDocumentKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Enter' && isOpen && !isForm) {
        event.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm, isOpen, isForm],
  );

  const handleCancel = (event?: React.MouseEvent | React.KeyboardEvent) => {
    onCancel(event, { warn: updated });
  };

  const handleCancelButtonClick = (
    event?: React.MouseEvent<HTMLElement>,
  ): void => {
    if (props.onCancelButtonClick) {
      props.onCancelButtonClick();
    } else {
      handleCancel(event);
    }
  };

  const handleConfirmButtonClick = (): Promise<unknown> | undefined =>
    handleConfirm();

  const handleChange = <V,>(
    data: FormData,
    name: string | undefined,
    value: V,
  ): void => {
    if (!isForm) {
      return;
    }
    const { warnIfFormChanges = true, onChange } = props;
    if (!updated && warnIfFormChanges) {
      setUpdated(true);
    }
    onChange?.(data, name, value);
  };

  const handleBackButtonClick = (): void => {
    if (onBack) {
      onBack();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [handleDocumentKeyDown]);

  const isDisabled = Boolean(props.isDisabled) || isHandlingConfirm || !isOpen;
  let content = (
    <>
      <div className={styles.content}>{children}</div>
      {!onSubmitAndClear ? null : (
        <div className={styles.submit_and_clear_mobile}>
          <Button
            type="submit"
            variant={confirmAndClearButtonVariant}
            disabled={Boolean(isDisabled)}
            aria={{ label: submitAndClearButtonText }}
            onClick={onSubmitAndClear}
            className={styles.submit_and_clear_button_mobile}
          >
            {submitAndClearButtonText}
          </Button>
        </div>
      )}
      <div className={styles.controls}>
        <Button
          variant={cancelButtonVariant}
          disabled={isDisabled}
          onClick={handleCancelButtonClick}
          uiKey="modal-cancel-button"
          aria={{ label: cancelButtonText }}
        >
          {cancelButtonText}
        </Button>
        <div className={styles.confirm_group}>
          {beforeConfirmElement}
          {onBack && (
            <Button
              variant="default"
              disabled={isDisabled}
              onClick={handleBackButtonClick}
              uiKey="modal-back-button"
              aria={{ label: backButtonText }}
            >
              {backButtonText}
            </Button>
          )}
          {isForm && !hideSendHint && <SendHint />}
          {!onSubmitAndClear ? null : (
            <Button
              type="submit"
              variant={confirmAndClearButtonVariant}
              disabled={Boolean(isDisabled)}
              aria={{ label: submitAndClearButtonText }}
              onClick={onSubmitAndClear}
              className={styles.submit_and_clear_button}
            >
              {submitAndClearButtonText}
            </Button>
          )}
          {!isForm && !onConfirm ? null : (
            <Button
              type="submit"
              variant={confirmButtonVariant}
              disabled={Boolean(isDisabled || isConfirmDisabled)}
              onClick={!isForm ? handleConfirmButtonClick : undefined}
              uiKey="modal-confirm-button"
              aria={{ label: confirmButtonText }}
            >
              {confirmButtonText}
            </Button>
          )}
        </div>
      </div>
    </>
  );
  if (isForm) {
    content = (
      <Form<FormData>
        onValidSubmit={handleConfirm}
        disabled={isDisabled}
        errors={props.validationErrors}
        onChange={handleChange}
        vertical={props.isFormVertical}
        submitByCommand
      >
        {content}
      </Form>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onOpen={onOpen}
      onRequestClose={handleCancel}
      onAfterClose={onAfterClose}
      headerText={headerText}
      variant={variant}
      size={size}
      className={className}
    >
      {content}
    </Modal>
  );
};

export default ControlsModal;
